import './App.vue.css';

  import { ref, reactive, computed, nextTick } from '../_snowpack/pkg/vue.js';
  import { useDisplay } from '../_snowpack/pkg/vuetify.js';
  import { calculate } from './lib/valueMath.js';
  import { labelsFor } from './data/labels.js';

  const defaultExport = {
      setup() {
          const sum = arr => arr.reduce((a,b) => a+b, 0);
          const formatValue = s => '$' + s.toString().replace(/\B(?=(\w{3})+(?!\w))/g, ",");
          const dataFor = (state, index) => {
              const value = state.series[index];
              const total = sum(state.series);
              const percentages = state.series.map(x => 100.0 * x/total);
              const percentage = percentages[index].toFixed(1);

              return {
                  percentage: (state.enabled ? percentage : '__') + '%',
                  value: formatValue(state.enabled ? value : '_____'),
              };
          };
          const label = (text, state, index) => {
              const data = dataFor(state, index);

              return `<span class="bvc-legend-title">${text}</span>
              <span class="bvc-legend-value">${data.value}</span>
              <span class="bvc-legend-percentage">${data.percentage}</span>`;
            };
          const CHART_OPTIONS = (state) => ({
              theme: {
                  monochrome: {
                      enabled: !state.enabled,
                      color: '#50596A',
                      shadeTo: 'light',
                      shadeIntensity: 0.65
                  }
              },
              labels: [
                  label('Improved Conversion Rates', state, 0), 
                  label('Improved Customer Retention', state, 1), 
                  label('Improved Campaign Efficiency', state, 2), 
                  label('Cost Savings with CleverTap', state, 3),
              ],
          });
          const clone = obj => JSON.parse(JSON.stringify(obj));

          const displaySize = useDisplay().name;
          const isMobile = computed(() => ['xs', 'sm'].includes(displaySize.value));

          const DEFAULT_STATE = [
              {
                  industry: null,
                  sector: null
              },
              {
                  averageOrderValue: 4,
                  annualCustomerValue: 35,
                  operatingMargin: 9,
                  churnRate: 15.00,
              },
              {
                  reachableUserBase: 50000000,
                  notificationsPerDay: 5,
                  avgClickthroughRate: 0.4,
                  conversionRate: 1.5,
                  effortPerCampaign: 30,
              },
              {
                  series: [2.8,6.7,1,2], // inactive percentages
                  enabled: false,
              }
          ];
          const stateFromStep = n => clone(
            DEFAULT_STATE.reduce(
                (acc, x, i) => Object.assign(acc, i >= n ? x : {}),
                {}
            )
          );
          const step = ref(0);
          const state = reactive({
              companyName: '',

              ...stateFromStep(0),
              chartOptions: {
                  ...CHART_OPTIONS({
                      series: [2.8,6.7,1,2],
                      enabled: false
                  }),

                  chart: {
                      width: 320,
                      height: 530,
                      type: 'donut',
                      fontFamily: 'Circular Std, Helvetica, Arial, sans-serif',
                      animations: { enabled: false },
                  },
                  stroke: { show:false },
                  tooltip: { enabled: false },
                  dataLabels: { enabled: false },
                  legend: {
                      show: true,
                      position: "bottom",
                      markers: {
                          radius: 0,
                          width: 16,
                          height: 29,
                      },
                      itemMargin: {
                          horizontal: 0,
                          vertical: 0
                      },
                      onItemHover: {
                          highlightDataSeries: false
                      },
                      labels: {
                          colors: ["#ffffff","#ffffff","#ffffff","#ffffff"],
                      }
                  },
                  states: {
                      active: {
                          filter: {
                              type: 'lighten',
                              value: 0,
                          }
                      },
                  },
                  plotOptions: {
                      pie: {
                          donut: {
                              size: '90%',
                              background: "#000D26",
                              labels: {
                                  show: true,
                                  name: {
                                      color: "#ffffff",
                                      formatter: function (w) {
                                          if (w.includes('__')) return '';
                                          return `${w
                                                  .replace(/<span.*?>(.*)?<\/span>/g, (_, x) => /\d/.test(x) ? '' : x)
                                                  .trim()}`;
                                      }
                                  },
                                  value: {
                                      show: true,
                                      fontWeight: "100",
                                      color: "#ffffff",
                                      formatter: function (w) {
                                          if (w < 10) return '';

                                          return formatValue(w);
                                      }
                                  }
                              }
                          }
                      }
                  }
              }
          });

          const updateChart = async () => {
              state.enabled = true;
              const result = calculate({
                  reachableUserBase: state.reachableUserBase,
                  notificationsPerDay: state.notificationsPerDay,
                  avgClickthroughRate: state.avgClickthroughRate/100,
                  conversionRate: state.conversionRate/100,
                  averageOrderValue: state.averageOrderValue,
                  operatingMargin: state.operatingMargin/100,

                  churnRate: state.churnRate/100,
                  annualCustomerValue: state.annualCustomerValue,

                  effortPerCampaign: state.effortPerCampaign
              });
              state.series = [
                  result.conversionRate,
                  result.customerRetention,
                  result.campaignEfficiency,
                  result.costSavings
              ];
              state.chartOptions = CHART_OPTIONS(state);
              state.total = sum(state.series);

              await nextTick();

              const [firstSeries, secondSeries] = document.querySelectorAll(".apexcharts-legend-text");
              secondSeries.click();
              firstSeries.click();
          };
          const nextDisabled = computed(() =>
              (step.value === 0 || isMobile.value) && (!state.industry || !state.sector)
          );
          const nextStep = () => {
              if (nextDisabled.value) {
                  return;
              } else if (isMobile.value) {
                  updateChart();
              } else if (step.value === 2) {
                  updateChart();
              } else {
                  step.value++;
              }
          };
          const prevStep = () => step.value--;
          const reset = async () => {
              const s = stateFromStep(step.value);
              Object.assign(state, s);
              step.value--;
              await nextTick();
              step.value++;
              state.chartOptions = CHART_OPTIONS(state);
          };

          const labels = computed(() => labelsFor(state.industry));

          return {
              step,
              nextStep,
              prevStep,
              reset,
              nextDisabled,

              state,

              setIndustry: (key) => {
                  state.industry = key;
                  state.sector = null;
              },
              setSector: (value) => {
                  state.sector = value;
                  if (!isMobile.value) {
                      nextStep();
                  }
              },

              labels,

              isMobile,

              formatValue,

              downloadReport: () => window.showDownloadForm({
                  ...state,
                  labels: labels.value,
                  formattedValues: {
                      total:  formatValue(state.total), 
                      conversionRates: dataFor(state, 0),
                      retention: dataFor(state, 1),
                      campaignEfficiency: dataFor(state, 2),
                      costSavings: dataFor(state, 3),
                  }
              }),

              onChartAnimationEnd: function(chartContext, config) {
                  const firstSeries = document.querySelector(".apexcharts-legend-text");

                  firstSeries.click();
              },
              onDataPointMouseEnter: function(event, chartContext, config) {
                  const seriesIndex = config.dataPointIndex;
                  const legend = document.querySelectorAll(".apexcharts-legend-text");
                  legend[seriesIndex].click();
              },
              onLegendClick: function(chartContext, seriesIndex, config) {
                  const items = document.querySelectorAll(".apexcharts-legend-series");
                  Array.from(items).forEach((x, i) => {
                      x.style.color = chartContext.theme.colors[i];
                      x.classList.toggle('bvc-active', state.enabled && i === seriesIndex);
                  });
              },
          };
      },
    data: () => ({
        industrySectors: {
            'E-commerce': [
                'Fashion & Retail',
                'Electronics',
                'Grocery',
                'Food Services',
                'Travel & Ticketing',
                'Quick commerce',
                'Education',
                'Health & Wellness',
                'On-demand',
                'Other Services'
            ],
            'Financial Services': [
                'Banking',
                'Fintech',
                'Insurance',
                'Broking',
                'Wallets',
                'Lending',
                'Other'
            ],
            'Subscriptions': [
                'Entertainment & OTT',
                'Media, News, & Publications',
                'Education',
                'Health & Wellness',
                'Other'
            ],
            'Gaming': [
                'Free-to-play gaming',
                'Real money gaming',
                'Other'
            ],
            'Other': [
                'Ad-driven',
                'Other'
            ]
        }
    })
  };

import { createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock, createVNode as _createVNode, TransitionGroup as _TransitionGroup, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "../_snowpack/pkg/vue.js"

const _withScopeId = n => (_pushScopeId("data-v-074b83cc"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "bvc-root" }
const _hoisted_2 = { class: "column" }
const _hoisted_3 = { class: "bvc-card" }
const _hoisted_4 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "bvc-card-top" }, [
  /*#__PURE__*/_createElementVNode("h1", { class: "bvc-card-header" }, "Business Value Calculator"),
  /*#__PURE__*/_createElementVNode("div", { class: "bvc-card-header-description" }, " Fill in the details to measure the impact of CleverTap on your business. ")
], -1))
const _hoisted_5 = { class: "bvc-card-body" }
const _hoisted_6 = {
  key: 0,
  class: "bvc-progress"
}
const _hoisted_7 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", null, "Industry & Sector", -1))
const _hoisted_8 = [
  _hoisted_7
]
const _hoisted_9 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("li", { class: "separator" }, null, -1))
const _hoisted_10 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", null, "Company Details", -1))
const _hoisted_11 = [
  _hoisted_10
]
const _hoisted_12 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("li", { class: "separator" }, null, -1))
const _hoisted_13 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", null, "Campaign Performance", -1))
const _hoisted_14 = [
  _hoisted_13
]
const _hoisted_15 = {
  class: "bvc-wizard-step",
  key: "0"
}
const _hoisted_16 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h2", { class: "bvc-h2" }, "What's your industry?", -1))
const _hoisted_17 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "bvc-description" }, "Please select one", -1))
const _hoisted_18 = { class: "bvc-radio-pills-container" }
const _hoisted_19 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h2", { class: "bvc-h2" }, " Which industry sector does your business specialize in? ", -1))
const _hoisted_20 = {
  class: "bvc-wizard-step pb-0",
  key: "1"
}
const _hoisted_21 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h2", { class: "bvc-h2" }, "Could you tell us more about your company?", -1))
const _hoisted_22 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "bvc-description" }, "Adjust the sliders or enter your figures to receive a customized business value estimate tailored to your company", -1))
const _hoisted_23 = {
  class: "bvc-wizard-step",
  key: "2"
}
const _hoisted_24 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h2", { class: "bvc-h2" }, "How are your present campaigns performing?", -1))
const _hoisted_25 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "bvc-description" }, "Adjust the sliders or enter your figures to receive a customized business value estimate tailored to your company", -1))
const _hoisted_26 = { class: "bvc-card-footer" }
const _hoisted_27 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "bvc-value-header" }, "Total Business Value with CleverTap*", -1))
const _hoisted_28 = { class: "bvc-value-total" }
const _hoisted_29 = ["disabled"]
const _hoisted_30 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("svg", {
  width: "25",
  height: "25",
  viewBox: "0 0 25 25",
  class: "v-icon notranslate v-theme--light v-icon--size-default v-icon--end",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}, [
  /*#__PURE__*/_createElementVNode("path", {
    d: "M21.851 15.4038V19.4038C21.851 19.9342 21.6402 20.4429 21.2652 20.818C20.8901 21.1931 20.3814 21.4038 19.851 21.4038H5.85095C5.32052 21.4038 4.81181 21.1931 4.43674 20.818C4.06167 20.4429 3.85095 19.9342 3.85095 19.4038V15.4038M7.85095 10.4038L12.851 15.4038M12.851 15.4038L17.851 10.4038M12.851 15.4038V3.40381",
    stroke: "currentColor",
    "stroke-width": "2",
    "stroke-linecap": "round",
    "stroke-linejoin": "round"
  })
], -1))
const _hoisted_31 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", { class: "bvc-value-disclaimer" }, "* Business Benefits as Total Present Value for Three Years.", -1))

export function render(_ctx, _cache) {
  const _component_v_btn = _resolveComponent("v-btn")
  const _component_v_select = _resolveComponent("v-select")
  const _component_bvc_slider = _resolveComponent("bvc-slider")
  const _component_v_col = _resolveComponent("v-col")
  const _component_v_spacer = _resolveComponent("v-spacer")
  const _component_v_row = _resolveComponent("v-row")
  const _component_v_container = _resolveComponent("v-container")
  const _component_v_icon = _resolveComponent("v-icon")
  const _component_v_tooltip = _resolveComponent("v-tooltip")
  const _component_ApexChart = _resolveComponent("ApexChart")
  const _component_v_app = _resolveComponent("v-app")

  return (_openBlock(), _createBlock(_component_v_app, null, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _hoisted_4,
            _createElementVNode("div", _hoisted_5, [
              (!_ctx.isMobile)
                ? (_openBlock(), _createElementBlock("ul", _hoisted_6, [
                    _createElementVNode("li", {
                      class: _normalizeClass({active: _ctx.step == 0})
                    }, [..._hoisted_8], 2),
                    _hoisted_9,
                    _createElementVNode("li", {
                      class: _normalizeClass({active: _ctx.step == 1})
                    }, [..._hoisted_11], 2),
                    _hoisted_12,
                    _createElementVNode("li", {
                      class: _normalizeClass({active: _ctx.step == 2})
                    }, [..._hoisted_14], 2)
                  ]))
                : _createCommentVNode("", true),
              _createVNode(_TransitionGroup, { name: "slide" }, {
                default: _withCtx(() => [
                  (_ctx.isMobile || _ctx.step == 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                        _hoisted_16,
                        _hoisted_17,
                        _createElementVNode("div", _hoisted_18, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.industrySectors, (_, key) => {
                            return (_openBlock(), _createBlock(_component_v_btn, {
                              variant: "flat",
                              rounded: "xl",
                              class: _normalizeClass(["text-none bvc-radio-pill", { active: key == _ctx.state.industry }]),
                              key: key,
                              onClick: $event => (_ctx.setIndustry(key))
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(key), 1)
                              ]),
                              _: 2
                            }, 1032, ["class", "onClick"]))
                          }), 128))
                        ]),
                        _hoisted_19,
                        _createVNode(_component_v_select, {
                          disabled: !_ctx.state.industry,
                          "hide-details": true,
                          label: !_ctx.state.industry ? 'Please select industry' : !_ctx.state.sector ? 'Please select one' : '',
                          items: _ctx.state.industry ? _ctx.industrySectors[_ctx.state.industry] : [],
                          "menu-props": {maxHeight: '250px'},
                          modelValue: _ctx.state.sector,
                          "onUpdate:modelValue": [
                            _cache[0] || (_cache[0] = $event => ((_ctx.state.sector) = $event)),
                            _ctx.setSector
                          ],
                          variant: "outlined"
                        }, null, 8, ["disabled", "label", "items", "modelValue", "onUpdate:modelValue"])
                      ]))
                    : _createCommentVNode("", true),
                  (_ctx.isMobile || _ctx.step == 1)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                        _hoisted_21,
                        _hoisted_22,
                        _createVNode(_component_v_container, { class: "px-0" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_row, { "no-gutters": "" }, {
                              default: _withCtx(() => [
                                _createVNode(_component_v_col, null, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_bvc_slider, {
                                      label: _ctx.labels.averageOrderValue.label,
                                      tooltip: _ctx.labels.averageOrderValue.description,
                                      unit: "$",
                                      modelValue: _ctx.state.averageOrderValue,
                                      "onUpdate:modelValue": _cache[1] || (_cache[1] = $event => ((_ctx.state.averageOrderValue) = $event)),
                                      min: "0",
                                      max: "500+"
                                    }, null, 8, ["label", "tooltip", "modelValue"])
                                  ]),
                                  _: 1
                                }),
                                _createVNode(_component_v_spacer),
                                _createVNode(_component_v_col, null, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_bvc_slider, {
                                      label: _ctx.labels.annualCustomerValue.label,
                                      tooltip: _ctx.labels.annualCustomerValue.description,
                                      unit: "$",
                                      modelValue: _ctx.state.annualCustomerValue,
                                      "onUpdate:modelValue": _cache[2] || (_cache[2] = $event => ((_ctx.state.annualCustomerValue) = $event)),
                                      min: "0",
                                      max: "1000+"
                                    }, null, 8, ["label", "tooltip", "modelValue"])
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            }),
                            _createVNode(_component_v_row, { "no-gutters": "" }, {
                              default: _withCtx(() => [
                                _createVNode(_component_v_col, null, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_bvc_slider, {
                                      label: _ctx.labels.operatingMargin.label,
                                      tooltip: _ctx.labels.operatingMargin.description,
                                      unit: "%",
                                      modelValue: _ctx.state.operatingMargin,
                                      "onUpdate:modelValue": _cache[3] || (_cache[3] = $event => ((_ctx.state.operatingMargin) = $event)),
                                      min: "<0",
                                      tickStep: "5",
                                      max: "20 or more"
                                    }, null, 8, ["label", "tooltip", "modelValue"])
                                  ]),
                                  _: 1
                                }),
                                _createVNode(_component_v_spacer),
                                _createVNode(_component_v_col, null, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_bvc_slider, {
                                      label: _ctx.labels.churnRate.label,
                                      tooltip: _ctx.labels.churnRate.description,
                                      unit: "%",
                                      modelValue: _ctx.state.churnRate,
                                      "onUpdate:modelValue": _cache[4] || (_cache[4] = $event => ((_ctx.state.churnRate) = $event)),
                                      min: "0",
                                      max: "100"
                                    }, null, 8, ["label", "tooltip", "modelValue"])
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })
                      ]))
                    : _createCommentVNode("", true),
                  (_ctx.isMobile || _ctx.step == 2)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
                        _hoisted_24,
                        _hoisted_25,
                        _createVNode(_component_v_container, { class: "px-0" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_row, { "no-gutters": "" }, {
                              default: _withCtx(() => [
                                _createVNode(_component_v_col, null, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_bvc_slider, {
                                      label: "Size of Reachable User Base",
                                      tooltip: "The total number of users who can be targeted or reached through campaigns.",
                                      modelValue: _ctx.state.reachableUserBase,
                                      "onUpdate:modelValue": _cache[5] || (_cache[5] = $event => ((_ctx.state.reachableUserBase) = $event)),
                                      min: "0",
                                      max: "100,000,000+"
                                    }, null, 8, ["modelValue"])
                                  ]),
                                  _: 1
                                }),
                                _createVNode(_component_v_spacer),
                                _createVNode(_component_v_col, null, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_bvc_slider, {
                                      label: "Notifications Sent Per User Per Day",
                                      tooltip: "Average number of notifications each user receives from your service daily.",
                                      modelValue: _ctx.state.notificationsPerDay,
                                      "onUpdate:modelValue": _cache[6] || (_cache[6] = $event => ((_ctx.state.notificationsPerDay) = $event)),
                                      min: "0",
                                      max: "10+"
                                    }, null, 8, ["modelValue"])
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            }),
                            _createVNode(_component_v_row, { "no-gutters": "" }, {
                              default: _withCtx(() => [
                                _createVNode(_component_v_col, null, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_bvc_slider, {
                                      label: "Average CTR for Ongoing Campaigns (%)",
                                      tooltip: "Percentage of recipients who click on a link in a campaign.",
                                      modelValue: _ctx.state.avgClickthroughRate,
                                      "onUpdate:modelValue": _cache[7] || (_cache[7] = $event => ((_ctx.state.avgClickthroughRate) = $event)),
                                      unit: "%",
                                      min: "0",
                                      max: "50+"
                                    }, null, 8, ["modelValue"])
                                  ]),
                                  _: 1
                                }),
                                _createVNode(_component_v_spacer),
                                _createVNode(_component_v_col, null, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_bvc_slider, {
                                      label: _ctx.labels.conversionRate.label,
                                      tooltip: _ctx.labels.conversionRate.description,
                                      modelValue: _ctx.state.conversionRate,
                                      "onUpdate:modelValue": _cache[8] || (_cache[8] = $event => ((_ctx.state.conversionRate) = $event)),
                                      unit: "%",
                                      min: "0",
                                      max: "50+"
                                    }, null, 8, ["label", "tooltip", "modelValue"])
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            }),
                            _createVNode(_component_v_row, { "no-gutters": "" }, {
                              default: _withCtx(() => [
                                _createVNode(_component_v_col, null, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_bvc_slider, {
                                      label: "FTE Effort Per Campaign (minutes)",
                                      tooltip: "Total full-time equivalent minutes spent by employees on a single campaign.",
                                      modelValue: _ctx.state.effortPerCampaign,
                                      "onUpdate:modelValue": _cache[9] || (_cache[9] = $event => ((_ctx.state.effortPerCampaign) = $event)),
                                      unit: "minutes",
                                      min: "0",
                                      max: "120"
                                    }, null, 8, ["modelValue"])
                                  ]),
                                  _: 1
                                }),
                                _createVNode(_component_v_spacer),
                                _createVNode(_component_v_col)
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })
                      ]))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              })
            ]),
            _createElementVNode("div", _hoisted_26, [
              _createElementVNode("button", {
                class: _normalizeClass(["c-btn c-btn-reverse text-none", { 'd-none': _ctx.isMobile || _ctx.step == 0 }]),
                onClick: _cache[10] || (_cache[10] = (...args) => (_ctx.prevStep && _ctx.prevStep(...args)))
              }, " Previous ", 2),
              _createElementVNode("button", {
                class: "c-btn c-btn-arrow regular-link ml-auto text-none",
                onClick: _cache[11] || (_cache[11] = (...args) => (_ctx.reset && _ctx.reset(...args)))
              }, [
                _createTextVNode(" Reset "),
                _createVNode(_component_v_icon, {
                  icon: "mdi-reload",
                  end: ""
                })
              ]),
              _createElementVNode("button", {
                class: "c-btn text-none",
                onClick: _cache[12] || (_cache[12] = (...args) => (_ctx.nextStep && _ctx.nextStep(...args)))
              }, [
                (_ctx.nextDisabled)
                  ? (_openBlock(), _createBlock(_component_v_tooltip, {
                      key: 0,
                      activator: "parent",
                      location: "top"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_ctx.isMobile ? 'Please fill out all fields' : 'Please complete this step'), 1)
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                _createTextVNode(" " + _toDisplayString(_ctx.isMobile || _ctx.step === 2 ? "Calculate" : "Next"), 1)
              ])
            ])
          ])
        ]),
        _createElementVNode("div", {
          class: _normalizeClass(["column column-2", { 'bvc-inactive': !_ctx.state.enabled }])
        }, [
          _createElementVNode("button", {
            class: "bvc-back-button",
            onClick: _cache[13] || (_cache[13] = $event => (_ctx.state.enabled = false))
          }, "Back"),
          _hoisted_27,
          _createElementVNode("div", _hoisted_28, _toDisplayString(_ctx.formatValue(_ctx.state.enabled ? _ctx.state.total : '______')), 1),
          _createVNode(_component_ApexChart, {
            class: _normalizeClass({ 'bvc-disabled': !_ctx.state.enabled }),
            width: "320",
            height: 530,
            type: "donut",
            onAnimationEnd: _ctx.onChartAnimationEnd,
            onDataPointMouseEnter: _ctx.onDataPointMouseEnter,
            onLegendClick: _ctx.onLegendClick,
            options: _ctx.state.chartOptions,
            series: _ctx.state.series
          }, null, 8, ["class", "onAnimationEnd", "onDataPointMouseEnter", "onLegendClick", "options", "series"]),
          _createElementVNode("button", {
            class: "c-btn mx-auto mt-5 mx-5 text-none bvc-main-cta",
            disabled: !_ctx.state.enabled,
            onClick: _cache[14] || (_cache[14] = (...args) => (_ctx.downloadReport && _ctx.downloadReport(...args)))
          }, [
            _createTextVNode(" Download business value report "),
            _hoisted_30
          ], 8, _hoisted_29),
          _hoisted_31
        ], 2)
      ])
    ]),
    _: 1
  }))
};

defaultExport.render = render;

defaultExport.__scopeId = "data-v-074b83cc";

export default defaultExport;